import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Plus, Trash2, X, Search, Package,
    CheckCircle2,
    FileText, ShoppingCart,
    Tag, Layers, ChevronRight, CheckCheck
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import secureStorage from "../utils/secureStorage";
import { api } from "../config/api";
import moment from 'moment';
import 'moment/locale/fr';
import { Avatar } from './StockUI';
import { inputClass } from '../ClientsTable/clientFormUI';
import AppleSelect from '../components/Common/AppleSelect';
import Button from '../components/Common/Button';
import Notification from "../components/Common/Notification";
import DateInput from "../components/Common/DateInput";

moment.locale('fr');

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
    pending: {
        label: 'En attente de confirmation',
        color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
        dot: 'bg-amber-400',
    },
    confirmed: {
        label: 'Confirmé',
        color: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        dot: 'bg-emerald-400',
    },
    cancelled: {
        label: 'Annulé',
        color: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
        dot: 'bg-red-400',
    },
};

const EMPTY_FORM = {
    provider_id: '',
    reference: '',
    note: '',
    received_at: moment().format('YYYY-MM-DD'),
    status: 'pending',
    items: []
};

// ─── TVA helpers ─────────────────────────────────────────────────────────────

// Taxes come serialized as {id, name, value} where value is a percentage ("20")
const toRate = (tax) => {
    if (!tax || typeof tax !== 'object' || tax.value === null || tax.value === '') return null;
    const n = Number(tax.value);
    return Number.isNaN(n) ? null : n;
};

// A variant rarely has its own tax: fall back to its product's
const defaultTaxRate = (product, variant) =>
    toRate(variant?.taxe) ?? toRate(variant?.taxes) ?? toRate(product?.taxes);

const round2 = (n) => Math.round(n * 100) / 100;

// Keep HT / TTC in sync with the line's TVA rate, from whichever field was just edited
const syncPrices = (item, field) => {
    const rate = parseFloat(item.tax_rate);
    if (Number.isNaN(rate)) return item;
    const ht = parseFloat(item.unit_cost);
    const ttc = parseFloat(item.unit_cost_ttc);
    const fromHt = () => ({ ...item, unit_cost_ttc: Number.isNaN(ht) ? '' : String(round2(ht * (1 + rate / 100))) });
    const fromTtc = () => ({ ...item, unit_cost: Number.isNaN(ttc) ? '' : String(round2(ttc / (1 + rate / 100))) });
    if (field === 'unit_cost') return fromHt();
    if (field === 'unit_cost_ttc') return fromTtc();
    return item;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const FormLabel = ({ children, required }) => (
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {children}
        {required && <span className="text-red-500 dark:text-red-400"> *</span>} :
    </label>
);

// Catalogue form field
const fieldClass = inputClass(false);

// Small field inside the items table
const cellInputClass = 'px-2.5 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary';

// Flat section separated by a rule, like the catalogue product form
const SectionCard = ({ title, icon: Icon, children }) => (
    <section className="pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700 first:border-t-0 first:pt-0">
        <h3 className="section-title flex items-center gap-2 mb-4">
            <Icon className="w-4 h-4" />
            {title}
        </h3>
        {children}
    </section>
);

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

// submitRequestKey / resetRequestKey: bumped by the Stock page header
// (« Enregistrer » / « Vider »), as on the catalogue forms
const StockReception = ({ submitRequestKey = 0, resetRequestKey = 0 }) => {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [receptionForm, setReceptionForm] = useState(EMPTY_FORM);
    const [searchTerm, setSearchTerm] = useState('');
    const [showProductSearch, setShowProductSearch] = useState(false);
    const searchRef = useRef(null);

    // Cached with React Query: the tab that hosts this form gets unmounted whenever the
    // user switches to another Stock tab, so a plain useEffect fetch would re-hit the API
    // (and re-show a blank/loading catalog) every single time they come back to "Réception".
    const fetchProviders = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const { data } = await api.get('/providers', { headers: { 'X-account-id': accountId.id } });
            return data.data || [];
        } catch {
            return [];
        }
    };
    const { data: providers = [], isError: providersError } = useQuery({
        queryKey: ['stock-reception-providers'],
        queryFn: fetchProviders,
        staleTime: 5 * 60 * 1000,
    });

    const fetchProducts = async () => {
        const accountId = await secureStorage.getItem("selectedAccount");
        const { data } = await api.get('/products?itemsPerPage=1000&is_stockable=true', { headers: { 'X-account-id': accountId.id } });
        // also filter client-side in case the API ignores the param
        const all = data.data || [];
        return all.filter(p => p.isStockable !== false);
    };
    const { data: products = [] } = useQuery({
        queryKey: ['stock-reception-products'],
        queryFn: fetchProducts,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (providersError) showNotif('error', 'Erreur lors du chargement des fournisseurs');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providersError]);

    useEffect(() => {
        if (showProductSearch && searchRef.current) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [showProductSearch]);

    const showNotif = (type, message) => {
        setNotification({ show: true, type, message });
        setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
    };

    const setField = (field, value) =>
        setReceptionForm(prev => ({ ...prev, [field]: value }));

    const resetForm = () => setReceptionForm(EMPTY_FORM);

    const addItem = (product, isVariant = false, variant = null, qty = 1) => {
        setReceptionForm(prev => {
            const pid = isVariant ? null : product.id;
            const vid = isVariant ? variant.id : null;
            const existing = prev.items.find(i => i.product_id === pid && i.variant_id === vid);
            if (existing) {
                return {
                    ...prev,
                    items: prev.items.map(i => i === existing
                        ? { ...i, quantity: (parseFloat(i.quantity) || 0) + qty }
                        : i)
                };
            }
            const taxRate = defaultTaxRate(product, isVariant ? variant : null);
            // Known purchase price of the product (variants have none of their own)
            const knownHt = !isVariant && product.purchasePriceHt != null ? parseFloat(product.purchasePriceHt) : NaN;
            const knownTtc = !isVariant && product.purchasePriceTtc != null ? parseFloat(product.purchasePriceTtc) : NaN;
            const unitCost = Number.isNaN(knownHt) ? '' : String(round2(knownHt));
            let unitCostTtc = Number.isNaN(knownTtc) ? '' : String(round2(knownTtc));
            if (unitCostTtc === '' && unitCost !== '' && taxRate !== null) {
                unitCostTtc = String(round2(knownHt * (1 + taxRate / 100)));
            }
            return {
            ...prev,
            items: [...prev.items, {
                // Date.now() alone can collide when adding several items back-to-back
                // (same millisecond) — that broke removeItem (it would drop both rows).
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                product_id: isVariant ? null : product.id,
                variant_id: isVariant ? variant.id : null,
                productName: isVariant ? `${product.name} — ${variant.name}` : product.name,
                quantity: qty,
                unit_cost: unitCost,
                unit_cost_ttc: unitCostTtc,
                tax_rate: taxRate !== null ? String(taxRate) : '',
                note: ''
            }]
            };
        });
        // Keep the catalog modal open — a reception usually has several line items,
        // and the modal's own UI (running counter, "Confirmer" footer button) is built
        // for adding them one after another without reopening the picker each time.
    };

    const removeItem = (id) =>
        setReceptionForm(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));

    const updateItem = (id, field, value) =>
        setReceptionForm(prev => ({
            ...prev,
            items: prev.items.map(i => i.id === id ? syncPrices({ ...i, [field]: value }, field) : i)
        }));

    const handleSubmit = async () => {
        if (!receptionForm.provider_id) return showNotif('error', 'Veuillez sélectionner un fournisseur');
        if (receptionForm.items.length === 0) return showNotif('error', 'Veuillez ajouter au moins un article');

        const invalid = receptionForm.items.filter(i =>
            !i.quantity || i.quantity <= 0 || !i.unit_cost || i.unit_cost <= 0
        );
        if (invalid.length > 0) return showNotif('error', 'Quantité et coût requis pour tous les articles');

        setIsLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.post('/purchase-orders', {
                provider_id: receptionForm.provider_id,
                reference: receptionForm.reference,
                note: receptionForm.note,
                received_at: receptionForm.received_at,
                status: receptionForm.status,
                items: receptionForm.items.map(i => ({
                    product_id: i.product_id,
                    variant_id: i.variant_id,
                    item_type: i.variant_id ? 'variant' : 'product',
                    quantity: parseFloat(i.quantity),
                    unit_cost: parseFloat(i.unit_cost),
                    unit_cost_ttc: i.unit_cost_ttc !== '' ? parseFloat(i.unit_cost_ttc) : null,
                    note: i.note
                }))
            }, { headers: { 'X-account-id': accountId.id, 'Content-Type': 'application/json' } });

            showNotif('success', 'Réception de stock enregistrée avec succès');
            resetForm();
            // Stock levels shown in the catalog picker (StockBadge) are now stale.
            queryClient.invalidateQueries({ queryKey: ['stock-reception-products'] });
        } catch (e) {
            showNotif('error', e.response?.data?.message || "Erreur lors de l'enregistrement");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (submitRequestKey > 0) handleSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitRequestKey]);

    useEffect(() => {
        if (resetRequestKey > 0) resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetRequestKey]);

    const totalCost = receptionForm.items.reduce((sum, i) =>
        sum + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_cost) || 0), 0
    );
    const totalCostTtc = receptionForm.items.reduce((sum, i) =>
        sum + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_cost_ttc) || 0), 0
    );

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8 pb-10">

            {/* Toast */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification(prev => ({...prev, show: false}))}
            />

            {/* ── Informations générales ─────────────────────────────────────── */}
            <SectionCard title="Informations générales" icon={FileText}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    {/* Fournisseur – full width on small */}
                    <div className="sm:col-span-2 lg:col-span-1">
                        <FormLabel required>Fournisseur</FormLabel>
                        <div className="relative">
                            <AppleSelect
                                value={receptionForm.provider_id}
                                onChange={e => setField('provider_id', e.target.value)}
                                className={`${fieldClass} pr-9 appearance-none`}
                            >
                                <option value="">Sélectionner un fournisseur…</option>
                                {providers.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </AppleSelect>
                        </div>
                    </div>

                    {/* Référence */}
                    <div>
                        <FormLabel>Référence</FormLabel>
                        <div className="relative">
                            <input
                                type="text"
                                value={receptionForm.reference}
                                onChange={e => setField('reference', e.target.value)}
                                placeholder="BC-2026-001"
                                className={fieldClass}
                            />
                        </div>
                    </div>

                    {/* Date */}
                    <div>
                        <FormLabel required>Date de réception</FormLabel>
                        <div className="relative">
                            <DateInput
                                required
                                value={receptionForm.received_at}
                                onChange={e => setField('received_at', e.target.value)}
                                className={fieldClass}
                            />
                        </div>
                    </div>

                    {/* Statut */}
                    <div>
                        <FormLabel required>Statut</FormLabel>
                        <div className="relative">
                            <AppleSelect
                                value={receptionForm.status}
                                onChange={e => setField('status', e.target.value)}
                                className={`${fieldClass} pr-9 appearance-none`}
                            >
                                {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                                    <option key={val} value={val}>{cfg.label}</option>
                                ))}
                            </AppleSelect>
                        </div>
                        <div className="mt-2">
                            <StatusBadge status={receptionForm.status} />
                        </div>
                    </div>

                    {/* Note */}
                    <div className="sm:col-span-2">
                        <FormLabel>Note</FormLabel>
                        <div className="relative">
                            <input
                                type="text"
                                value={receptionForm.note}
                                onChange={e => setField('note', e.target.value)}
                                placeholder="Livraison partielle, instructions spéciales…"
                                className={fieldClass}
                            />
                        </div>
                    </div>
                </div>
            </SectionCard>

            {/* ── Articles ───────────────────────────────────────────────────── */}
            <SectionCard
                title={`Articles${receptionForm.items.length > 0 ? ` (${receptionForm.items.length})` : ''}`}
                icon={ShoppingCart}
            >
                {/* Toolbar */}
                <div className="flex items-center justify-between mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {receptionForm.items.length === 0
                            ? 'Aucun article ajouté'
                            : `${receptionForm.items.length} article${receptionForm.items.length > 1 ? 's' : ''}`}
                    </p>
                    <Button variant="tinted" size="sm" icon={Plus} onClick={() => setShowProductSearch(true)}>
                        Ajouter un article
                    </Button>
                </div>

                {/* Empty state */}
                {receptionForm.items.length === 0 ? (
                    <div
                        onClick={() => setShowProductSearch(true)}
                        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg py-10 flex flex-col items-center gap-3 cursor-pointer hover:border-primary hover:bg-primary/5 transition group"
                    >
                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:bg-primary/10 flex items-center justify-center transition">
                            <Package className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-primary transition" />
                        </div>
                        <div className="text-center">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 group-hover:text-primary transition">
                                Cliquez pour ajouter un article
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Recherchez dans votre catalogue produits</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            <table className="w-full text-sm">
                                <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Produit</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-28">Qté</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-32">Coût/u HT (DH)</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-24">TVA %</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-32">Prix d'achat TTC (DH)</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-28">Total</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Note</th>
                                    <th className="px-3 py-3 w-10"></th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {receptionForm.items.map((item, idx) => (
                                    <tr key={item.id} className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-3 py-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                                                    {idx + 1}
                                                </div>
                                                <span className="font-medium text-gray-800 dark:text-gray-100 text-sm">{item.productName}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3">
                                            <input
                                                type="number" min="1"
                                                value={item.quantity}
                                                onChange={e => updateItem(item.id, 'quantity', e.target.value)}
                                                className={`w-24 ${cellInputClass} text-center font-medium`}
                                            />
                                        </td>
                                        <td className="px-3 py-3">
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={item.unit_cost}
                                                onChange={e => updateItem(item.id, 'unit_cost', e.target.value)}
                                                placeholder="0.00"
                                                className={`w-28 ${cellInputClass} text-right`}
                                            />
                                        </td>
                                        <td className="px-3 py-3">
                                            <span title="TVA du produit — le TTC est calculé automatiquement"
                                                  className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                {item.tax_rate !== '' ? `${item.tax_rate} %` : '—'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3">
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={item.unit_cost_ttc}
                                                onChange={e => updateItem(item.id, 'unit_cost_ttc', e.target.value)}
                                                placeholder="0.00"
                                                className={`w-28 ${cellInputClass} text-right`}
                                            />
                                        </td>
                                        <td className="px-3 py-3">
                                                <span className="font-semibold text-gray-800 dark:text-gray-100">
                                                    {((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0)).toFixed(2)}
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">DH</span>
                                                </span>
                                        </td>
                                        <td className="px-3 py-3">
                                            <input
                                                type="text"
                                                value={item.note}
                                                onChange={e => updateItem(item.id, 'note', e.target.value)}
                                                placeholder="Note…"
                                                className={`w-36 ${cellInputClass}`}
                                            />
                                        </td>
                                        <td className="px-3 py-3">
                                            <button
                                                onClick={() => removeItem(item.id)}
                                                className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden space-y-3">
                            {receptionForm.items.map((item, idx) => (
                                <div key={item.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                                    <div className="flex items-start justify-between mb-3 gap-2">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                                                {idx + 1}
                                            </div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{item.productName}</p>
                                        </div>
                                        <button
                                            onClick={() => removeItem(item.id)}
                                            className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition flex-shrink-0"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">Quantité</p>
                                            <input
                                                type="number" min="1"
                                                value={item.quantity}
                                                onChange={e => updateItem(item.id, 'quantity', e.target.value)}
                                                className={`w-full ${cellInputClass} text-center font-medium`}
                                            />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">Coût/u HT (DH)</p>
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={item.unit_cost}
                                                onChange={e => updateItem(item.id, 'unit_cost', e.target.value)}
                                                placeholder="0.00"
                                                className={`w-full ${cellInputClass} text-right`}
                                            />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">TVA %</p>
                                            <p className="px-3 py-2 text-sm font-medium text-right text-gray-700 dark:text-gray-300">
                                                {item.tax_rate !== '' ? `${item.tax_rate} %` : '—'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">Prix d'achat TTC (DH)</p>
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={item.unit_cost_ttc}
                                                onChange={e => updateItem(item.id, 'unit_cost_ttc', e.target.value)}
                                                placeholder="0.00"
                                                className={`w-full ${cellInputClass} text-right`}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pt-2.5 border-t border-gray-200 dark:border-gray-700">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">Total ligne</span>
                                        <span className="text-sm font-bold text-primary">
                                            {((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0)).toFixed(2)} DH
                                        </span>
                                    </div>

                                    <input
                                        type="text"
                                        value={item.note}
                                        onChange={e => updateItem(item.id, 'note', e.target.value)}
                                        placeholder="Note sur cet article…"
                                        className={`mt-2 w-full ${cellInputClass}`}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Total général — same look as the counters strip of the lists */}
                        <div className="mt-5 flex justify-end">
                            <div className="grid grid-cols-2 border border-gray-200 dark:border-gray-700 rounded-lg divide-x divide-gray-200 dark:divide-gray-700 min-w-[16rem]">
                                <div className="px-4 sm:px-6 py-3">
                                    <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total HT</span>
                                    <span className="block text-xl font-bold text-gray-900 dark:text-gray-100">{totalCost.toFixed(2)} DH</span>
                                </div>
                                <div className="px-4 sm:px-6 py-3">
                                    <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total TTC</span>
                                    <span className="block text-xl font-bold text-primary">{totalCostTtc.toFixed(2)} DH</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </SectionCard>

            {isLoading && (
                <p className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    Enregistrement de la réception…
                </p>
            )}

            {/* ── Product search modal ───────────────────────────────────────── */}
            {showProductSearch && (
                <ProductSearchModal
                    products={products}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchRef={searchRef}
                    addItem={addItem}
                    alreadyAdded={receptionForm.items}
                    onClose={() => { setShowProductSearch(false); setSearchTerm(''); }}
                />
            )}
        </div>
    );
};

// ─── Product Search Modal ─────────────────────────────────────────────────────

// Same colours as the stock status badges of the Stock list
const StockBadge = ({ stock }) => {
    if (stock === undefined || stock === null) return null;
    const pill = 'text-[10px] font-semibold px-2 py-0.5 rounded-full';
    if (stock <= 0)
        return <span className={`${pill} bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400`}>Rupture</span>;
    if (stock <= 5)
        return <span className={`${pill} bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400`}>{stock} en stock</span>;
    return <span className={`${pill} bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400`}>{stock} en stock</span>;
};

// "7*6111234567890" → 7 unités du code 6111234567890 (saisie type caisse : quantité, *, scan)
const parseScanInput = (raw) => {
    const m = String(raw).match(/^\s*(\d+(?:[.,]\d+)?)\s*\*\s*(.*)$/);
    if (!m) return { qty: null, code: String(raw).trim() };
    const qty = parseFloat(m[1].replace(',', '.'));
    return { qty: qty > 0 ? qty : null, code: m[2].trim() };
};

const ProductSearchModal = ({ products, searchTerm, setSearchTerm, searchRef, addItem, alreadyAdded, onClose }) => {
    const [expandedIds, setExpandedIds] = useState({});
    const [activeCategory, setActiveCategory] = useState('all');
    const [justAdded, setJustAdded] = useState({});
    const [scanQty, setScanQty] = useState('1');
    const qtyValue = () => {
        const n = parseFloat(scanQty);
        return n > 0 ? n : 1;
    };

    // Catégories uniques extraites des produits
    const categories = React.useMemo(() => {
        const categoriesMap = new Map();
        products.forEach(p => {
            if (p.category) {
                const catId = typeof p.category === 'object' ? p.category.id : p.category;
                const catName = typeof p.category === 'object' ? p.category.name : p.category;
                if (!categoriesMap.has(catId)) {
                    categoriesMap.set(catId, catName);
                }
            }
        });
        return Array.from(categoriesMap.entries()).map(([id, name]) => ({ id, name }));
    }, [products]);

    // Ids déjà dans le panier (pour badge)
    const addedIds = new Set(alreadyAdded.map(i => i.product_id || i.variant_id));

    // Memoized on [products, searchTerm, activeCategory] only — NOT on `alreadyAdded`,
    // which changes on every add/remove while this modal stays open, and would otherwise
    // force a re-filter of up to 1000 products for each click.
    const filtered = useMemo(() => products.filter(p => {
        const term = parseScanInput(searchTerm).code.toLowerCase();
        const matchSearch = p.name.toLowerCase().includes(term)
            || (p.barcode && String(p.barcode).toLowerCase().includes(term))
            || (p.variants || []).some(v => v.name?.toLowerCase().includes(term)
                || (v.barcode && String(v.barcode).toLowerCase().includes(term)));
        const productCategoryId = typeof p.category === 'object' ? p.category.id : p.category;
        const matchCat = activeCategory === 'all' || productCategoryId === activeCategory;
        return matchSearch && matchCat;
    }), [products, searchTerm, activeCategory]);

    const toggleExpand = (id) =>
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));

    const handleAdd = (product, isVariant = false, variant = null, qtyOverride = null) => {
        const feedbackId = isVariant ? `v-${variant.id}` : `p-${product.id}`;
        addItem(product, isVariant, variant, qtyOverride ?? qtyValue());
        setJustAdded(prev => ({ ...prev, [feedbackId]: true }));
        setTimeout(() => setJustAdded(prev => ({ ...prev, [feedbackId]: false })), 1200);
    };

    const totalAdded = alreadyAdded.length;

    // Lecteur code-barres : le scan saisit le code puis Entrée → ajoute la quantité choisie
    const handleSearchKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        const { qty: typedQty, code } = parseScanInput(searchTerm);
        if (!code) return;
        for (const p of products) {
            const variant = (p.variants || []).find(v => v.barcode && String(v.barcode) === code);
            if (variant || (!(p.variants?.length > 0) && p.barcode && String(p.barcode) === code)) {
                handleAdd(p, !!variant, variant, typedQty);
                setSearchTerm('');
                setScanQty('1');
                searchRef.current?.focus();
                return;
            }
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            onClick={e => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white dark:bg-gray-800 w-full sm:max-w-xl rounded-t-2xl sm:rounded-lg shadow-lg flex flex-col max-h-[90vh] sm:max-h-[80vh] overflow-hidden">

                {/* ── Header ───────────────────────────────────────────────── */}
                <div className="px-5 pt-5 pb-0 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
                    {/* Drag handle */}
                    <div className="w-10 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-4 sm:hidden" />

                    <div className="relative text-center px-10 mb-4">
                        <h3 className="text-lg font-medium text-gray-800 dark:text-white">Catalogue produits</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {filtered.length} produit{filtered.length !== 1 ? 's' : ''}
                            {searchTerm && ` pour "${searchTerm}"`}
                        </p>
                        <button
                            onClick={onClose}
                            aria-label="Fermer"
                            className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Search bar */}
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <input
                            ref={searchRef}
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Rechercher, ou scanner (ex : 7* puis scan)…"
                            className={`${fieldClass} pl-9 pr-9`}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Quantité appliquée à chaque ajout / scan */}
                    <div className="flex items-center justify-between gap-3 mb-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                        <label htmlFor="scan-qty" className="text-xs font-medium text-gray-600 dark:text-gray-300">
                            Quantité du prochain scan
                        </label>
                        <div className="flex items-center gap-1.5">
                            <button type="button" onClick={() => setScanQty(String(Math.max(1, qtyValue() - 1)))}
                                    className="w-7 h-7 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary">−</button>
                            <input id="scan-qty" type="number" min="1" value={scanQty}
                                   onChange={e => setScanQty(e.target.value)}
                                   onFocus={e => e.target.select()}
                                   onKeyDown={e => { if (e.key === 'Enter' || e.key === '*') { e.preventDefault(); searchRef.current?.focus(); } }}
                                   className={`w-16 ${cellInputClass} py-1 text-center font-semibold`}/>
                            <button type="button" onClick={() => setScanQty(String(qtyValue() + 1))}
                                    className="w-7 h-7 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary">+</button>
                        </div>
                    </div>

                    {/* Category chips */}
                    {categories.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide -mx-1 px-1">
                            <button
                                onClick={() => setActiveCategory('all')}
                                className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition
                                    ${activeCategory === 'all'
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary'}`}
                            >
                                Tout
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveCategory(cat.id)}
                                    className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition
                                        ${activeCategory === cat.id
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary'}`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Results ───────────────────────────────────────────────── */}
                <div className="overflow-y-auto flex-1">
                    {filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                <Package className="w-7 h-7 text-gray-300 dark:text-gray-400" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aucun produit trouvé</p>
                                {searchTerm && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                        Essayez avec un autre terme
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filtered.map(product => {
                                const hasVariants = product.variants?.length > 0;
                                const isExpanded = expandedIds[product.id];
                                const feedbackId = `p-${product.id}`;
                                const isJustAdded = justAdded[feedbackId];
                                const isInBasket = addedIds.has(product.id);

                                return (
                                    <div key={product.id}>
                                        {/* ── Product row ── */}
                                        <div className={`flex items-center gap-3 px-5 py-3 transition-colors group
                                            ${isInBasket ? 'bg-primary/5' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                                        >
                                            {/* Avatar */}
                                            <Avatar name={product.name} color={product.color} size="sm" />

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{product.name}</p>
                                                    {isInBasket && (
                                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary flex-shrink-0">
                                                            ✓ Ajouté
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                    {product.category && (
                                                        <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                                                            <Tag className="w-2.5 h-2.5" />
                                                            {typeof product.category === 'object' ? product.category.name : product.category}
                                                        </span>
                                                    )}
                                                    {product.barcode && (
                                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">#{product.barcode}</span>
                                                    )}
                                                    <StockBadge stock={product.currentStock} />
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                {hasVariants && (
                                                    <button
                                                        onClick={() => toggleExpand(product.id)}
                                                        className={`flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-lg border transition
                                                            ${isExpanded
                                                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                                                            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary'}`}
                                                    >
                                                        <Layers className="w-3.5 h-3.5" />
                                                        <span>{product.variants.length}</span>
                                                        <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                    </button>
                                                )}
                                                {!hasVariants && (
                                                <button
                                                    onClick={() => handleAdd(product)}
                                                    className={`w-8 h-8 flex items-center justify-center rounded-lg border transition
                                                        ${isJustAdded
                                                        ? 'bg-primary border-primary text-white'
                                                        : 'border-primary text-primary hover:bg-primary hover:text-white active:scale-95'}`}
                                                >
                                                    {isJustAdded
                                                        ? <CheckCircle2 className="w-4 h-4" />
                                                        : <Plus className="w-4 h-4" />}
                                                </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Variants (expandable) ── */}
                                        {hasVariants && isExpanded && (
                                            <div className="divide-y divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
                                                {product.variants.map(variant => {
                                                    const vFeedbackId = `v-${variant.id}`;
                                                    const vJustAdded = justAdded[vFeedbackId];
                                                    const vInBasket = addedIds.has(variant.id);

                                                    return (
                                                        <div
                                                            key={variant.id}
                                                            className={`flex items-center gap-3 pl-14 pr-5 py-2.5 transition-colors group
                                                                ${vInBasket ? 'bg-primary/5' : 'hover:bg-gray-100 dark:hover:bg-gray-700/50'}`}
                                                        >
                                                            {/* Variant avatar */}
                                                            <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                                {variant.name.substring(0, 2).toUpperCase()}
                                                            </div>

                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">{variant.name}</p>
                                                                    {vInBasket && (
                                                                        <span className="text-[10px] font-semibold text-primary flex-shrink-0">✓</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    {variant.barcode && (
                                                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">#{variant.barcode}</span>
                                                                    )}
                                                                    <StockBadge stock={variant.currentStock} />
                                                                </div>
                                                            </div>

                                                            <button
                                                                onClick={() => handleAdd(product, true, variant)}
                                                                className={`w-7 h-7 flex items-center justify-center rounded-lg border transition flex-shrink-0
                                                                    ${vJustAdded
                                                                    ? 'bg-primary border-primary text-white'
                                                                    : 'border-primary text-primary hover:bg-primary hover:text-white active:scale-95'}`}
                                                            >
                                                                {vJustAdded
                                                                    ? <CheckCircle2 className="w-3 h-3" />
                                                                    : <Plus className="w-3 h-3" />}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── Footer — « count | Confirmer » like the catalogue popups ── */}
                <div className="flex-shrink-0 grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                    <p className="flex items-center justify-center gap-1.5 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {totalAdded > 0
                            ? <><CheckCheck className="w-4 h-4 text-primary" /><span className="font-medium text-primary">{totalAdded} article{totalAdded > 1 ? 's' : ''}</span></>
                            : 'Aucun article'}
                    </p>
                    <button
                        onClick={onClose}
                        className="py-4 text-primary font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Confirmer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StockReception;
